import { type JSX, useState } from 'react';

import type { TelemetryEvent } from '@schema/telemetry.js';

import type { BuilderStore } from '../state.js';

import { TextField, ToggleField } from './Field.js';

export interface TelemetryEditorProps {
  store: BuilderStore;
}

const EVENT_CATEGORIES: readonly { value: TelemetryEvent; description: string }[] = [
  { value: 'tileLaunch',        description: 'Tile tap → app/web/agent action.' },
  { value: 'profileSwitch',     description: 'Profile activated (schedule, manual).' },
  { value: 'configError',       description: 'Zod parse failure or invalid asset ref.' },
  { value: 'licenseError',      description: 'License missing / expired / tenant mismatch.' },
  { value: 'agentAction',       description: "MDM agent dispatch outcome." },
  { value: 'remoteConfigPoll',  description: 'Remote-config fetch (success / failure / 304).' },
  { value: 'audit',             description: 'Admin gesture / PIN entry / overlay open.' },
];

/**
 * Telemetry sink editor (Enterprise feature: `telemetry.sink`).
 *
 * The whole config.telemetry block is optional. Empty state offers a CTA
 * to add it; once added, the editor exposes:
 *   • enabled (master switch)
 *   • endpoint (URL — Zod requires .url() when present)
 *   • authTokenRef (secret reference — *never* a plain token)
 *   • events[] (category allowlist)
 *
 * Reminder up top: tokens go in via `secret://` refs that the build
 * pipeline resolves. The hint copy is deliberate — admins paste raw
 * tokens into config fields all the time, and Zod can't stop them.
 */
export function TelemetryEditor({ store }: TelemetryEditorProps): JSX.Element {
  const telemetry = store.config.telemetry;

  if (telemetry === undefined) {
    return (
      <div className="editor">
        <header className="editor__head">
          <h2 className="editor__title">Telemetry</h2>
          <p className="editor__sub">
            Forward launcher events to an Enterprise telemetry sink. Requires
            the <strong>telemetry.sink</strong> entitlement at runtime.{' '}
            <a href="https://mobilauncher.com/docs/telemetry" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
              Read the docs →
            </a>
          </p>
        </header>
        <fieldset className="editor__group">
          <legend className="editor__group-label">Telemetry</legend>
          <p className="editor__hint">
            Not configured. Add the block to start forwarding events.
          </p>
          <button
            type="button"
            className="add-tile__btn"
            onClick={() => store.patchTelemetry({ enabled: false, events: [] })}
          >
            + Add telemetry block
          </button>
        </fieldset>
      </div>
    );
  }

  function toggleEvent(value: TelemetryEvent): void {
    if (telemetry === undefined) return;
    const has = telemetry.events.includes(value);
    const next = has ? telemetry.events.filter((e) => e !== value) : [...telemetry.events, value];
    store.patchTelemetry({ ...telemetry, events: next });
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Telemetry</h2>
        <p className="editor__sub">
          Auth tokens use <code>secret://</code> refs that the build pipeline
          resolves — never paste a raw token here.{' '}
          <a
            href="https://mobilauncher.com/docs/telemetry"
            target="_blank"
            rel="noopener noreferrer"
            className="editor__doc-link"
          >
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Sink</legend>
        <ToggleField
          label="Enabled"
          hint="Master switch; off ⇒ launcher emits nothing even if events are checked."
          value={telemetry.enabled}
          onChange={(v) => store.patchTelemetry({ ...telemetry, enabled: v })}
        />
        <TextField
          label="Endpoint URL"
          mono
          hint="HTTPS endpoint. Empty ⇒ events are dropped client-side."
          value={telemetry.endpoint ?? ''}
          onChange={(v) => {
            if (v.length > 0) store.patchTelemetry({ ...telemetry, endpoint: v });
            else {
              const { endpoint: _o, ...rest } = telemetry;
              void _o;
              store.patchTelemetry(rest);
            }
          }}
        />
        <TextField
          label="Auth token ref"
          mono
          hint="Secret reference, e.g., secret://acme/telemetry"
          value={telemetry.authTokenRef ?? ''}
          onChange={(v) => {
            if (v.length > 0) store.patchTelemetry({ ...telemetry, authTokenRef: v });
            else {
              const { authTokenRef: _o, ...rest } = telemetry;
              void _o;
              store.patchTelemetry(rest);
            }
          }}
        />
        <TestSinkButton endpoint={telemetry.endpoint} />
        <button
          type="button"
          className="builder__btn builder__btn--ghost"
          onClick={() => store.patchTelemetry(undefined)}
          title="Remove the telemetry block entirely"
        >
          Remove telemetry block
        </button>
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Event categories ({telemetry.events.length})</legend>
        <p className="editor__hint">
          Only checked categories are emitted. Empty list ⇒ telemetry is a no-op
          even when enabled.
        </p>
        <ul className="event-checklist">
          {EVENT_CATEGORIES.map((cat) => {
            const checked = telemetry.events.includes(cat.value);
            return (
              <li key={cat.value} className="event-checklist__row">
                <label className="event-checklist__label">
                  <input
                    type="checkbox"
                    className="event-checklist__check"
                    checked={checked}
                    onChange={() => toggleEvent(cat.value)}
                  />
                  <span className="event-checklist__name">{cat.value}</span>
                </label>
                <span className="event-checklist__desc">{cat.description}</span>
              </li>
            );
          })}
        </ul>
      </fieldset>
    </div>
  );
}

/**
 * One-shot test event. POSTs a synthetic { category: 'audit', name:
 * 'portalTestFire' } event to the configured endpoint with an optional
 * Bearer token. The token is paste-in / one-time — we never persist it
 * here (the real token lives in the MDM's secret-injection pipeline).
 *
 * Caveats:
 *   - CORS — same story as the remote-config probe. On-device runs in
 *     WebView and ignores CORS, so a browser fetch failure isn't a
 *     verdict.
 *   - The `authTokenRef` in the config is a `secret://` reference;
 *     the Portal can't resolve it. Customers pasting a real token to
 *     test is the workaround.
 */
function TestSinkButton({
  endpoint,
}: {
  endpoint: string | undefined;
}): JSX.Element {
  type Result =
    | { kind: 'ok'; status: number }
    | { kind: 'err'; message: string };
  const [token, setToken] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [showInput, setShowInput] = useState(false);

  async function run(): Promise<void> {
    if (endpoint === undefined || endpoint.length === 0) return;
    setBusy(true);
    setResult(null);
    try {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (token.length > 0) headers['authorization'] = `Bearer ${token}`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          events: [
            {
              category: 'audit',
              name: 'portalTestFire',
              ts: Date.now(),
              attrs: { source: 'portal-test-fire' },
            },
          ],
        }),
      });
      setResult({ kind: 'ok', status: res.status });
    } catch (e) {
      setResult({ kind: 'err', message: e instanceof Error ? e.message : 'fetch failed' });
    } finally {
      setBusy(false);
    }
  }

  const disabled = endpoint === undefined || endpoint.length === 0 || busy;
  const okStatus = result?.kind === 'ok' && result.status >= 200 && result.status < 300;
  const authStatus =
    result?.kind === 'ok' && (result.status === 401 || result.status === 403);

  return (
    <div className="field">
      {!showInput ? (
        <button
          type="button"
          className="add-tile__btn"
          onClick={() => setShowInput(true)}
          disabled={endpoint === undefined || endpoint.length === 0}
        >
          Send test event
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label className="field">
            <span className="field__label">Bearer token (optional — one-shot, not stored)</span>
            <input
              type="password"
              className="field__input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="paste a real token to test the auth path"
              autoComplete="off"
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              className="add-tile__btn"
              onClick={() => void run()}
              disabled={disabled}
            >
              {busy ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              className="builder__btn builder__btn--ghost"
              onClick={() => {
                setShowInput(false);
                setToken('');
                setResult(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {result?.kind === 'ok' && (
        <p className="field__hint" style={{ marginTop: '0.5rem' }}>
          HTTP <strong>{result.status}</strong>
          {okStatus && (
            <>
              {' — '}
              <strong style={{ color: '#5eead4' }}>sink accepted the event</strong>
            </>
          )}
          {authStatus && (
            <>
              {' — auth rejected. The sink is reachable; your token is the suspect. On-device, the launcher uses the secret-ref from the config, which the MDM pipeline resolves at deploy time.'}
            </>
          )}
          {!okStatus && !authStatus && (
            <> — non-2xx; sink received the request but rejected it (check your sink logs).</>
          )}
        </p>
      )}
      {result?.kind === 'err' && (
        <p className="field__hint" style={{ marginTop: '0.5rem', color: '#fca5a5' }}>
          {result.message}
          {result.message.includes('Failed to fetch') &&
            ' — typically a CORS issue. On-device fetches from WebView don\'t enforce CORS, so the launcher itself may still succeed.'}
        </p>
      )}
    </div>
  );
}
